import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Check, X, ChevronRight, Shield, ExternalLink, XCircle, AlertTriangle, FileSignature, Eye, Hourglass } from 'lucide-react';
import { CollapsibleSection } from '../components/CollapsibleSection';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { FinalApprovalDialog } from '../components/FinalApprovalDialog';

export default function PendingReviewsPage() {
  const navigate = useNavigate();
  const { isAdmin, user: currentUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ndaApprovals, setNdaApprovals] = useState([]);
  const [ndaBusy, setNdaBusy] = useState(null);
  const [approveDeal, setApproveDeal] = useState(null);

  const load = () => {
    api.getPendingReviews()
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
    api.getNdaApprovals()
      .then(res => setNdaApprovals(res.data.approvals || []))
      .catch(() => toast.error('NDA-Freigaben konnten nicht geladen werden'));
  };
  useEffect(() => { load(); }, []);

  const approveNda = async (a) => {
    if (!window.confirm(`NDA für „${a.company || a.subject}" freigeben? Es wird sofort signiert an den Broker gesendet.`)) return;
    setNdaBusy(a.id);
    try {
      await api.approveNdaApproval(a.id);
      toast.success('NDA freigegeben — signiert & an den Broker gesendet ✓');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Freigabe fehlgeschlagen'); }
    setNdaBusy(null);
  };

  const rejectNda = async (a) => {
    const reason = window.prompt('Grund der Ablehnung (geht als Benachrichtigung an den Anstoßenden):');
    if (reason === null) return;
    if (reason.trim().length < 3) { toast.error('Bitte kurz begründen'); return; }
    setNdaBusy(a.id);
    try {
      await api.rejectNdaApproval(a.id, reason.trim());
      toast.success('Freigabe abgelehnt — der Anstoßende wurde benachrichtigt');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    setNdaBusy(null);
  };

  const cancelNda = async (a) => {
    setNdaBusy(a.id);
    try {
      await api.cancelNdaApproval(a.id);
      toast.success('Freigabe-Anfrage zurückgezogen');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    setNdaBusy(null);
  };

  const [pdfView, setPdfView] = useState(null);

  const viewNdaPdf = async (a) => {
    try {
      const r = a.kind === 'project' ? await api.getProjectNdaFile(a.id) : await api.getProposalNdaFile(a.id);
      setPdfView({ name: a.nda_filename || 'NDA.pdf', url: URL.createObjectURL(r.data) });
    } catch {
      toast.error('NDA-PDF konnte nicht geladen werden');
    }
  };

  const closePdfView = () => {
    if (pdfView?.url) URL.revokeObjectURL(pdfView.url);
    setPdfView(null);
  };

  const handleConfirm = async (type, id, deal) => {
    if (type === 'approval' && deal) { setApproveDeal(deal); return; }
    try {
      const fn = type === 'approval' ? api.confirmApproval : type === 'contact' ? api.confirmContact
        : type === 'skip' ? api.confirmSkipOutreach : api.confirmPass;
      const res = await fn(id);
      toast.success(res.data.message);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleOverride = async (type, id) => {
    try {
      const fn = type === 'approval' ? api.overrideApproval : type === 'contact' ? api.overrideContact
        : type === 'skip' ? api.overrideSkipOutreach : api.overridePass;
      await fn(id);
      toast.success('Cancelled');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const SKIP_TARGET_LABELS = { contacted: 'Contacted', response_received: 'Response received', first_call_meeting: 'First Call / Meeting' };
  const rawSections = [
    { key: 'to_approve', title: '→ OLTA Approved', type: 'approval', color: 'blue', items: data?.to_approve || [], proposedField: 'approval_proposed' },
    { key: 'to_contact', title: '→ Contacted', type: 'contact', color: 'blue', items: data?.to_contact || [], proposedField: 'contact_proposed' },
    { key: 'to_skip', title: 'Outreach überspringen', type: 'skip', color: 'amber', items: data?.to_skip || [], proposedField: 'skip_outreach_proposed' },
    { key: 'to_pass', title: '→ Passed', type: 'pass', color: 'red', items: data?.to_pass || [], proposedField: 'pass_proposed' },
  ];
  // Own proposals are NOT pending for the proposer — they wait on the OTHER approvers.
  const isMine = (deal, field) => currentUser && (deal[field] || {}).proposed_by_id === currentUser.id;
  // Only approvers (Max/Talu) see other people's proposals — Luis (26.10.2026) never has to confirm anything.
  const isReviewer = !!data?.is_reviewer || isAdmin;
  const permFor = (deal, type) => (deal.review_permissions || {})[type] || {};
  const waitingOn = (perm) => (perm.pending_from || []).map(u => u.name.split(' ')[0]).join(' oder ');
  // The main list holds ONLY what this user may confirm; proposals waiting on someone else (e.g. Talu sees
  // an OLTA approval that only Max gives) go to a collapsed "Wartet auf andere" overview (Talu, 26.10.2026).
  const sections = rawSections.map(s => ({ ...s, items: isReviewer ? s.items.filter(d => !isMine(d, s.proposedField) && permFor(d, s.type).can_confirm) : [] }));
  const waitingOthers = isReviewer ? rawSections.flatMap(s =>
    s.items.filter(d => !isMine(d, s.proposedField) && !permFor(d, s.type).can_confirm).map(d => ({ ...d, __section: s }))) : [];
  const myProposals = rawSections.flatMap(s =>
    s.items.filter(d => isMine(d, s.proposedField)).map(d => ({ ...d, __section: s })));
  const visibleNdas = ndaApprovals.filter(a => a.is_own || a.can_approve);

  const myInactive = data?.my_inactive || [];
  const total = sections.reduce((n, s) => n + s.items.length, 0)
    + myInactive.length + visibleNdas.filter(a => a.can_approve).length;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="pending-reviews-page">
      <div>
        <h1 className="font-heading text-2xl text-foreground tracking-tight">My Pending Reviews</h1>
        <p className="text-sm text-muted-foreground mt-1">{total} item{total !== 1 ? 's' : ''} awaiting your attention</p>
      </div>

      {total === 0 && myProposals.length === 0 && visibleNdas.length === 0 && waitingOthers.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-12 text-center">
          <Check className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-foreground font-medium">All clear!</p>
          <p className="text-xs text-muted-foreground mt-1">No pending reviews right now.</p>
        </div>
      ) : (
        <>
        {myInactive.length > 0 && (
          <CollapsibleSection
            storageKey="reviews.inactive"
            icon={AlertTriangle}
            iconClass="text-amber-400"
            title="Pending Decision — inactive claims"
            count={myInactive.length}
            className="border-amber-500/30"
            testId="inactive-claims-section"
          >
            {myInactive.map(deal => (
              <div key={deal.id} className="px-4 py-3 border-b border-border last:border-b-0 flex items-center gap-3" data-testid={`inactive-claim-${deal.id}`}>
                <div className="flex-1 min-w-0">
                  <button onClick={() => navigate(`/projects/${deal.id}`)}
                    className="text-sm font-medium text-foreground hover:text-amber-400 transition-colors truncate block">
                    {deal.random_project_name || deal.name}
                    {deal.company_name && (
                      <span className="text-xs text-muted-foreground font-normal"> · {deal.company_name}</span>
                    )}
                  </button>
                  <p className="text-[10px] text-amber-400/70 mt-0.5">
                    Inactive since {deal.inactivity_reminded_at ? new Date(deal.inactivity_reminded_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'} · keep working on it or it will be auto-released.
                  </p>
                </div>
                <button onClick={() => navigate(`/projects/${deal.id}`)}
                  className="text-amber-400/50 hover:text-amber-400 transition-colors p-1" data-testid={`inactive-open-${deal.id}`}>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </CollapsibleSection>
        )}
        {visibleNdas.length > 0 && (
          <CollapsibleSection
            storageKey="reviews.broker_ndas"
            icon={FileSignature}
            iconClass="text-violet-400"
            title="Broker NDAs — Freigabe zum Gegenzeichnen"
            count={visibleNdas.length}
            className="border-violet-500/30"
            testId="broker-ndas-section"
          >
            {visibleNdas.map((a, i) => (
              <div key={a.id} className="px-4 py-3 border-b border-border last:border-b-0" data-testid={`nda-approval-${i}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-[240px]">
                    <p className="text-sm font-medium text-foreground">
                      {a.company || a.subject}
                      {a.company && <span className="text-xs text-muted-foreground font-normal"> · {a.subject}</span>}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {a.kind === 'project' ? 'Empfänger' : 'Broker'}: {a.broker_name} ({a.broker_email}) · NDA: {a.nda_filename}
                      {a.kind === 'project' && (
                        <Link to={`/projects/${a.id}`} className="text-sky-400 hover:underline ml-1.5"
                          data-testid={`nda-approval-deal-link-${i}`}>Deal öffnen →</Link>
                      )}
                    </p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <div className="w-5 h-5 rounded-full bg-violet-600/20 flex items-center justify-center text-violet-400 text-[9px] font-bold font-mono shrink-0">
                        {a.requested_by?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <span className="text-xs text-foreground font-medium">{a.requested_by}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {`möchte unterschreiben als „${a.signature_name}“ · `}{a.requested_at ? new Date(a.requested_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                    {a.review ? (
                      <div className="mt-2 bg-secondary/30 border border-border/60 rounded-sm p-2" data-testid={`nda-approval-review-${i}`}>
                        <div className="flex items-center gap-1.5 flex-wrap mb-1">
                          <span className={`text-[9px] font-mono border rounded px-1.5 py-0.5 uppercase ${a.review.recommendation === 'sign'
                            ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                            : 'border-red-500/40 bg-red-500/10 text-red-300'}`}>
                            KI-Review: {a.review.recommendation === 'sign' ? 'unterschriftsreif' : 'Anmerkungen nötig'}
                          </span>
                          {(a.review.red_flags || []).map((f, j) => (
                            <span key={j} className="text-[9px] font-mono border border-red-500/40 bg-red-500/10 text-red-300 rounded px-1.5 py-0.5">🔴 {f.title}</span>
                          ))}
                          {(a.review.yellow_flags || []).map((f, j) => (
                            <span key={j} className="text-[9px] font-mono border border-amber-500/40 bg-amber-500/10 text-amber-300 rounded px-1.5 py-0.5">🟡 {f.title}</span>
                          ))}
                        </div>
                        <p className="text-[10px] text-muted-foreground line-clamp-2">{a.review.summary}</p>
                      </div>
                    ) : (
                      <p className="text-[10px] text-amber-400/80 mt-1.5" data-testid={`nda-approval-noreview-${i}`}>
                        Kein KI-NDA-Review vorhanden — bitte das NDA-PDF selbst prüfen.
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 ml-auto">
                    <Button variant="outline" size="sm" onClick={() => viewNdaPdf(a)} data-testid={`nda-approval-view-${i}`}
                      className="border-border text-muted-foreground hover:text-foreground rounded-sm text-[10px] h-7 px-2">
                      <Eye className="w-3 h-3 mr-1" /> NDA ansehen
                    </Button>
                    {a.is_own ? (
                      <>
                        <span className="text-[10px] text-muted-foreground italic">Wartet auf Freigabe</span>
                        <Button variant="ghost" size="sm" onClick={() => cancelNda(a)} disabled={ndaBusy === a.id}
                          data-testid={`nda-approval-cancel-${i}`}
                          className="text-muted-foreground hover:text-red-400 rounded-sm text-[10px] h-7 px-2">
                          <X className="w-3 h-3 mr-0.5" /> Zurückziehen
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" onClick={() => approveNda(a)} disabled={ndaBusy === a.id}
                          data-testid={`nda-approval-approve-${i}`}
                          className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm text-[10px] h-7 px-2.5">
                          {ndaBusy === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-0.5" /> Freigeben & senden</>}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => rejectNda(a)} disabled={ndaBusy === a.id}
                          data-testid={`nda-approval-reject-${i}`}
                          className="border-red-500/40 text-red-400 hover:bg-red-500/10 rounded-sm text-[10px] h-7 px-2">
                          <X className="w-3 h-3 mr-0.5" /> Ablehnen
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CollapsibleSection>
        )}
        {sections.map(section => section.items.length > 0 && (
          <CollapsibleSection
            key={section.key}
            storageKey={`reviews.${section.key}`}
            icon={section.type === 'pass' ? XCircle : Shield}
            iconClass={section.type === 'pass' ? 'text-red-400' : section.type === 'skip' ? 'text-amber-400' : 'text-blue-400'}
            title={section.title}
            count={section.items.length}
            testId={`reviews-section-${section.key}`}
          >
            {section.items.map(deal => {
              const proposed = deal[section.proposedField] || {};
              const isProposer = currentUser && proposed.proposed_by_id === currentUser.id;
              const alreadyConfirmed = currentUser && (proposed.confirmations || []).some(c => c.id === currentUser?.id);
              const perm = permFor(deal, section.type);
              return (
                <div key={deal.id} className="px-4 py-3 border-b border-border last:border-b-0 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <button onClick={() => navigate(`/projects/${deal.id}`)}
                      className="text-sm font-medium text-foreground hover:text-blue-400 transition-colors truncate block">
                      {deal.name}
                      {deal.company_name && deal.company_name !== deal.name && (
                        <span className="text-xs text-muted-foreground font-normal" data-testid={`review-company-${deal.id}`}> · {deal.company_name}</span>
                      )}
                    </button>
                    <div className="flex items-center gap-1.5 mt-1">
                      <div className="w-5 h-5 rounded-full bg-blue-600/20 flex items-center justify-center text-blue-400 text-[9px] font-bold font-mono shrink-0">
                        {proposed.proposed_by?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <span className="text-xs text-foreground font-medium">{proposed.proposed_by}</span>
                      <span className="text-[10px] text-muted-foreground">·</span>
                      <span className="text-[10px] text-muted-foreground">{new Date(proposed.proposed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
                      {deal.owner_name && <><span className="text-[10px] text-muted-foreground">·</span><span className="text-[10px] text-muted-foreground">Owner: {deal.owner_name}</span></>}
                    </div>
                    {(section.type === 'pass' || section.type === 'skip') && proposed.reason && (
                      <p className={`text-[10px] mt-0.5 ml-6 ${section.type === 'skip' ? 'text-amber-400/70' : 'text-red-400/60'}`}>
                        {section.type === 'skip' && <span className="font-medium">→ {SKIP_TARGET_LABELS[proposed.target_stage] || proposed.target_stage}: </span>}
                        "{proposed.reason.slice(0, 60)}{proposed.reason.length > 60 ? '...' : ''}"
                      </p>
                    )}
                    {section.type === 'approval' && proposed.note && (
                      <p className="text-[10px] mt-0.5 ml-6 text-sky-300/90" data-testid={`review-approval-note-${deal.id}`}>
                        <span className="font-medium">{(proposed.proposed_by || '').split(' ')[0]}'s Note:</span> "{proposed.note}"
                      </p>
                    )}
                    {(proposed.confirmations || []).length > 0 && (
                      <p className="text-[9px] text-amber-400 mt-0.5 ml-6">
                        Confirmed by: {proposed.confirmations.map(c => c.name).join(', ')}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {isProposer ? (
                      <span className="text-[10px] text-muted-foreground italic">Awaiting review</span>
                    ) : alreadyConfirmed ? (
                      <span className="text-[10px] text-amber-400 italic">You confirmed</span>
                    ) : !perm.can_confirm ? (
                      <span className="text-[10px] text-muted-foreground italic" data-testid={`review-waiting-${deal.id}`}>
                        {waitingOn(perm) ? `Wartet auf ${waitingOn(perm)}` : 'Keine Bestätigung nötig'}
                      </span>
                    ) : (
                      <Button onClick={() => handleConfirm(section.type, deal.id, deal)} size="sm"
                        data-testid={`confirm-${section.type}-${deal.id}`}
                        className={`${section.type === 'pass' ? 'bg-red-600 hover:bg-red-500' : section.type === 'skip' ? 'bg-amber-600 hover:bg-amber-500' : 'bg-blue-600 hover:bg-blue-500'} text-white rounded-sm text-[10px] h-7 px-2.5`}>
                        <Check className="w-3 h-3 mr-0.5" /> {section.type === 'pass' ? 'Confirm Pass' : section.type === 'skip' ? 'Skip freigeben' : section.type === 'approval' ? 'Prüfen & freigeben' : 'Approve'}
                      </Button>
                    )}
                    {isAdmin && isProposer && section.type !== 'skip' && (
                      <Button onClick={async () => {
                        try { const res = await api.bypassApproval(deal.id, section.type === 'to_approve' ? 'approve' : section.type === 'to_contact' ? 'contact' : 'pass');
                          toast.success(res.data.message); load(); }
                        catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                      }} size="sm" variant="outline"
                        className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 rounded-sm text-[10px] h-7 px-2">
                        Bypass
                      </Button>
                    )}
                    {(perm.can_confirm || isAdmin) && (
                      <Button onClick={() => handleOverride(section.type, deal.id)} size="sm" variant="ghost"
                        className="text-muted-foreground hover:text-foreground rounded-sm text-[10px] h-7 px-2">
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                    <button onClick={() => navigate(`/projects/${deal.id}`)}
                      className="text-muted-foreground/30 hover:text-foreground transition-colors p-1">
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </CollapsibleSection>
        ))}
        {waitingOthers.length > 0 && (
          <CollapsibleSection
            storageKey="reviews.waiting_others"
            icon={Hourglass}
            iconClass="text-muted-foreground"
            title="Wartet auf andere — nicht deine Freigabe"
            count={waitingOthers.length}
            defaultOpen={false}
            testId="waiting-others-section"
          >
            {waitingOthers.map(deal => {
              const proposed = deal[deal.__section.proposedField] || {};
              const perm = permFor(deal, deal.__section.type);
              return (
                <div key={`${deal.id}-${deal.__section.key}`} data-testid={`waiting-other-${deal.id}`}
                  className="px-4 py-3 border-b border-border last:border-b-0 flex items-center gap-3 opacity-80">
                  <div className="flex-1 min-w-0">
                    <button onClick={() => navigate(`/projects/${deal.id}`)}
                      className="text-sm font-medium text-foreground hover:text-blue-400 transition-colors truncate block">
                      {deal.name}
                      {deal.company_name && deal.company_name !== deal.name && (
                        <span className="text-xs text-muted-foreground font-normal"> · {deal.company_name}</span>
                      )}
                    </button>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {deal.__section.title} · von {proposed.proposed_by} am {proposed.proposed_at ? new Date(proposed.proposed_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'short' }) : '—'}
                      {proposed.note && <span className="text-sky-300/80"> · „{proposed.note}“</span>}
                      {proposed.reason && <span className="text-red-400/70"> · „{proposed.reason.slice(0, 60)}{proposed.reason.length > 60 ? '…' : ''}“</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[10px] text-muted-foreground italic" data-testid={`review-waiting-${deal.id}`}>
                      {waitingOn(perm) ? `Wartet auf ${waitingOn(perm)}` : perm.self_final ? 'Freigabe durch den Vorschlagenden' : 'Keine Bestätigung nötig'}
                    </span>
                    {isAdmin && (
                      <Button onClick={() => handleOverride(deal.__section.type, deal.id)} size="sm" variant="ghost"
                        title="Vorschlag abbrechen (Admin)"
                        className="text-muted-foreground hover:text-red-400 rounded-sm text-[10px] h-7 px-2">
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                    <button onClick={() => navigate(`/projects/${deal.id}`)}
                      className="text-muted-foreground/30 hover:text-foreground transition-colors p-1">
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </CollapsibleSection>
        )}
        {myProposals.length > 0 && (
          <CollapsibleSection
            storageKey="reviews.mine"
            icon={Hourglass}
            iconClass="text-muted-foreground"
            title="Von dir vorgeschlagen — wartet auf die anderen"
            count={myProposals.length}
            testId="my-proposals-section"
          >
            {myProposals.map(deal => {
              const proposed = deal[deal.__section.proposedField] || {};
              return (
                <div key={`${deal.id}-${deal.__section.key}`} data-testid={`my-proposal-${deal.id}`}
                  className="px-4 py-3 border-b border-border last:border-b-0 flex items-center gap-3 opacity-80">
                  <div className="flex-1 min-w-0">
                    <button onClick={() => navigate(`/projects/${deal.id}`)}
                      className="text-sm font-medium text-foreground hover:text-blue-400 transition-colors truncate block">
                      {deal.name}
                      {deal.company_name && deal.company_name !== deal.name && (
                        <span className="text-xs text-muted-foreground font-normal"> · {deal.company_name}</span>
                      )}
                    </button>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {deal.__section.title} · vorgeschlagen am {proposed.proposed_at ? new Date(proposed.proposed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}
                      {(proposed.confirmations || []).length > 0 && ` · bestätigt: ${proposed.confirmations.map(c => c.name).join(', ')}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[10px] text-muted-foreground italic" data-testid={`my-proposal-waiting-${deal.id}`}>
                      {waitingOn(permFor(deal, deal.__section.type)) ? `Wartet auf ${waitingOn(permFor(deal, deal.__section.type))}` : 'Wartet auf Freigabe'}
                    </span>
                    {isAdmin && deal.__section.type !== 'skip' && (
                      <Button onClick={async () => {
                        try { const res = await api.bypassApproval(deal.id, deal.__section.type === 'approval' ? 'approve' : deal.__section.type); 
                          toast.success(res.data.message); load(); }
                        catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                      }} size="sm" variant="outline"
                        className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 rounded-sm text-[10px] h-7 px-2">
                        Bypass
                      </Button>
                    )}
                    <Button onClick={() => handleOverride(deal.__section.type, deal.id)} size="sm" variant="ghost"
                      title="Vorschlag zurückziehen"
                      className="text-muted-foreground hover:text-red-400 rounded-sm text-[10px] h-7 px-2">
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </CollapsibleSection>
        )}
        </>
      )}
      <Dialog open={!!pdfView} onOpenChange={(o) => !o && closePdfView()}>
        <DialogContent className="bg-card border-border rounded-sm max-w-4xl" data-testid="nda-pdf-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground flex items-center gap-2 text-sm">
              <FileSignature className="w-4 h-4 text-violet-400" /> {pdfView?.name}
            </DialogTitle>
          </DialogHeader>
          {pdfView && (
            <iframe src={pdfView.url} title={pdfView.name} className="w-full h-[70vh] rounded-sm border border-border bg-white" data-testid="nda-pdf-frame" />
          )}
        </DialogContent>
      </Dialog>
      {approveDeal && <FinalApprovalDialog project={approveDeal} onClose={() => setApproveDeal(null)} onDone={load} />}
    </div>
  );
}
